import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useEscapeState } from './useEscapeState';
import { rooms } from './rooms';
import { getPuzzleStatus, isRoomComplete } from './engine';
import { SceneStage } from './SceneStage';
import { PuzzleList } from './PuzzleList';
import { PuzzleRouter } from './PuzzleRouter';
import { InventoryPanel } from './InventoryPanel';
import { ProgressPips } from './ProgressPips';
import { Modal } from './Modal';
import { Toast } from './Toast';
import type { SceneHotspot, HotspotScavengerState } from './types';
import './escape.css';

interface ToastState {
  message: string;
  icon?: string;
}

export function EscapeRoomShell() {
  const { state, begin, finish, reset, advanceRoom, markRoomComplete, patchPuzzle, grantItem, revealNextHint } =
    useEscapeState();

  const [activePuzzleId, setActivePuzzleId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const currentRoom = rooms[state.currentRoomIndex];

  // Auto-mark room complete
  useEffect(() => {
    if (state.completedRooms[state.currentRoomIndex]) return;
    if (isRoomComplete(state.currentRoomIndex, rooms, state)) {
      markRoomComplete(state.currentRoomIndex);
    }
  }, [state, state.currentRoomIndex, markRoomComplete]);

  // Show a toast
  const showToast = useCallback((message: string, icon?: string) => {
    setToast({ message, icon });
  }, []);

  // Scene hotspot click (Room 1 scavenger)
  function handleHotspotClick(hotspot: SceneHotspot) {
    const scavengerDef = currentRoom.puzzles.find(p => p.params.type === 'hotspot-scavenger');
    if (!scavengerDef) return;

    patchPuzzle<HotspotScavengerState>(scavengerDef.id, s => {
      if (s.visitedIds.includes(hotspot.id)) return s;
      const nextVisited = [...s.visitedIds, hotspot.id];

      // Auto-solve when enough visited
      const solved = nextVisited.length >= (scavengerDef.params as { requiredVisitCount: number }).requiredVisitCount;

      return { ...s, visitedIds: nextVisited, solved };
    });

    if (hotspot.kind === 'item' && hotspot.itemId) {
      if (!state.inventory.includes(hotspot.itemId)) {
        grantItem(hotspot.itemId);
        showToast(`Found: ${hotspot.label} glyph!`, 'box-open');
      }
    }
  }

  // Puzzle solved callback
  function handlePuzzleSolved(puzzleId: string) {
    const def = currentRoom.puzzles.find(p => p.id === puzzleId);
    if (def) {
      showToast(`${def.title} — solved!`, 'circle-check');
    }
  }

  // Get scavenger visited IDs for scene display
  const scavengerState = (() => {
    const def = currentRoom.puzzles.find(p => p.params.type === 'hotspot-scavenger');
    if (!def) return null;
    return state.puzzleState[def.id] as HotspotScavengerState | undefined;
  })();

  const sceneHotspots = (() => {
    const def = currentRoom.puzzles.find(p => p.params.type === 'hotspot-scavenger');
    if (!def) return [];
    return (def.params as { hotspots: SceneHotspot[] }).hotspots;
  })();

  const activePuzzleDef = activePuzzleId
    ? currentRoom.puzzles.find(p => p.id === activePuzzleId) ?? null
    : null;

  // ── Intro ────────────────────────────────────────────────────────────────────

  if (!state.started) {
    return (
      <div className="escape-intro">
        <div className="escape-intro__card">
          <p className="escape-intro__eyebrow">Interactive Experience</p>
          <h1 className="escape-intro__title">Escape the Frame</h1>
          <p className="escape-intro__subtitle">
            Three rooms. Multiple puzzles. No timer. Progress is saved.
          </p>
          <button
            className="escape-btn escape-btn--primary escape-btn--large"
            type="button"
            onClick={begin}
          >
            Begin
          </button>
          <p className="escape-intro__hint">
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Progress auto-saves in your browser.
          </p>
        </div>
      </div>
    );
  }

  // ── Completion ───────────────────────────────────────────────────────────────

  if (state.finished) {
    return (
      <div className="escape-completion">
        <div className="escape-completion__card">
          <i className="fa-solid fa-door-open escape-completion__icon" aria-hidden="true" />
          <p className="escape-completion__message">
            You didn't escape the room.
            <br />
            You escaped a frame.
          </p>
          <div className="escape-completion__actions">
            <button
              className="escape-btn escape-btn--secondary"
              type="button"
              onClick={reset}
            >
              <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Restart
            </button>
            <Link to="/book" className="escape-btn escape-btn--primary">
              Read the Book{' '}
              <i className="fa-solid fa-book-open" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Room ─────────────────────────────────────────────────────────────────────

  const roomComplete = state.completedRooms[state.currentRoomIndex];
  const isLastRoom = state.currentRoomIndex === rooms.length - 1;

  return (
    <div className="escape-shell">
      {/* Header */}
      <div className="escape-shell__header">
        <ProgressPips
          total={rooms.length}
          current={state.currentRoomIndex}
          completed={state.completedRooms}
        />
        <div className="escape-shell__meta">
          <h2 className="escape-shell__room-title">{currentRoom.title}</h2>
          <p className="escape-shell__narrative">{currentRoom.narrative}</p>
        </div>
        <button
          className="escape-reset-btn"
          type="button"
          onClick={reset}
          aria-label="Reset and start over"
        >
          <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Reset
        </button>
      </div>

      {/* Body */}
      <div className="escape-shell__body">
        {/* Scene */}
        <div className="escape-scene-col">
          <SceneStage
            backgroundImage={currentRoom.backgroundImage}
            hotspots={sceneHotspots}
            visitedIds={scavengerState?.visitedIds ?? []}
            onHotspotClick={handleHotspotClick}
          />
        </div>

        {/* Panel */}
        <div className="escape-panel-col">
          <InventoryPanel inventory={state.inventory} />

          <div className="escape-panel-section">
            <p className="escape-panel-section__heading">Puzzles</p>
            <PuzzleList
              puzzles={currentRoom.puzzles}
              getStatus={id => getPuzzleStatus(id, rooms, state)}
              onOpen={setActivePuzzleId}
            />
          </div>

          {roomComplete && (
            <div className="escape-panel-complete">
              <p className="escape-panel-complete__msg">
                <i className="fa-solid fa-circle-check" aria-hidden="true" /> Room complete!
              </p>
              <button
                className="escape-btn escape-btn--primary"
                type="button"
                onClick={() => {
                  if (isLastRoom) finish();
                  else advanceRoom();
                }}
              >
                {isLastRoom ? (
                  <><i className="fa-solid fa-door-open" aria-hidden="true" /> Finish</>
                ) : (
                  <><i className="fa-solid fa-arrow-right" aria-hidden="true" /> Next Room</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bounded modal for active puzzle */}
      {activePuzzleDef && (
        <Modal title={activePuzzleDef.title} onClose={() => setActivePuzzleId(null)}>
          <p className="escape-modal-narrative">{activePuzzleDef.narrative}</p>
          <PuzzleRouter
            def={activePuzzleDef}
            state={state}
            onSolve={(puzzleId) => {
              handlePuzzleSolved(puzzleId);
              // Close modal after short delay if it auto-solves
              setTimeout(() => setActivePuzzleId(null), 1200);
            }}
            patchPuzzle={patchPuzzle}
            grantItem={grantItem}
            hintsRevealed={state.hintsRevealed[activePuzzleDef.id] ?? 0}
            onRevealHint={() => revealNextHint(activePuzzleDef.id)}
          />
        </Modal>
      )}

      {/* Bounded toast */}
      {toast && (
        <Toast
          message={toast.message}
          icon={toast.icon}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
